import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, X, Play, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaUpload({ serviceId, media = [], onMediaUpdate, getAuthHeaders }) {
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      // Vérifier la taille (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} : Fichier trop volumineux (max 50MB)`);
        errorCount++;
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(
          `${API_URL}/api/services/${serviceId}/media`,
          formData,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        successCount++;
      } catch (error) {
        console.error('Erreur upload:', error);
        errorCount++;
        toast.error(`${file.name} : ${error.response?.data?.detail || 'Erreur'}`);
      }
    }

    setUploading(false);
    event.target.value = '';

    if (successCount > 0) {
      toast.success(`${successCount} média(s) ajouté(s) avec succès`);
      onMediaUpdate();
    }
  };

  const handleDelete = async (publicId) => {
    console.log('🗑️ Tentative de suppression:', publicId);
    
    if (!window.confirm('Supprimer ce média ?')) {
      console.log('❌ Suppression annulée par l\'utilisateur');
      return;
    }

    console.log('⏳ Suppression en cours...');
    
    try {
      const url = `${API_URL}/api/services/${serviceId}/media/${encodeURIComponent(publicId)}`;
      console.log('📡 URL:', url);
      
      const response = await axios.delete(url, getAuthHeaders());
      console.log('✅ Réponse:', response.data);
      
      toast.success('Média supprimé');
      onMediaUpdate();
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      console.error('Détails:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Photos & Vidéos</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => document.getElementById(`file-upload-${serviceId}`).click()}
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? 'Upload en cours...' : 'Ajouter'}
        </Button>
        <input
          id={`file-upload-${serviceId}`}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/jpg,image/webp,video/mp4,video/quicktime"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      {media && media.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{media.length} média(s) ajouté(s)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {media.map((item, index) => (
              <div key={index} className="space-y-2">
                <Card className="overflow-hidden">
                  <CardContent className="p-0 aspect-square">
                    {item.resource_type === 'video' ? (
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                        controls
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={`Média ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </CardContent>
                </Card>
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🖱️ CLIC DÉTECTÉ !');
                    handleDelete(item.public_id);
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Supprimer cette image
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground text-center">
              Aucun média. Ajoutez des photos ou vidéos de vos réalisations.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
